import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { updateStepState, updateActiveStep, setPdfBody, clearOrder, STEP } from '../store/orderFlow';
import { usePlaceOrder } from '../requests/rental';
import { getOrderPayload, getPDFBody, getStepInDirection } from '../views/RentalRequest/rentalrequest.utils';
import { useDialog } from './OrderDialog';
import OrderFlowContext from './orderprovider.context';
import PropTypes from 'prop-types';
import { clearPreloadPDFConfig } from "../assets/common/common";

export const OrderFlowProvider = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { showDialog } = useDialog();
  const { steps, activeStep } = useSelector((state) => state.orderFlow);
  const { isPending, mutateAsync: placeRentalRequest } = usePlaceOrder({ 
    onSuccess: (res) => {
      const orderId = res.orderId?.[0];
      const pdfBody = getPDFBody(steps, orderId);
      dispatch(setPdfBody(pdfBody));
      dispatch(clearOrder());

      navigate("/bedankt");
    },
    onError: (error) => {
      console.error('Order placement failed:', error);
      showDialog('error', 
        "Het versturen van de aanvraag is niet gelukt vanwege een technisch probleem, probeer het nogmaals. "
          + "Blijf je deze melding houden neem dan contact met ons op via onze chat rechts onderin.");
    }
  });
  const currentStep = activeStep || STEP.LOCATION;
  const currentStepState = steps?.[currentStep] || {};

  // Update active step based on direction and set search params
  const updateStep = ({ step = null, direction = 'forward' } = {}) => {
    let newStep = currentStep;

    if (!step) {
      if (currentStep === STEP.SUMMARY && direction === 'forward') return;
      if (currentStep === STEP.LOCATION && direction === 'backward') return;
  
      newStep = getStepInDirection(currentStep, direction);
    } else {
      newStep = step;
    }

    const params = new URLSearchParams(location.search);
    steps?.[STEP.PRODUCT_CARDS].name &&  params.set('huurmiddel', steps?.[STEP.PRODUCT_CARDS].name);

    params.set('step', newStep);
    dispatch(updateActiveStep(newStep));
    navigate({ search: params.toString() }, { replace: true });
  };

  const updateOrder = (stepName, data) => {
    dispatch(updateStepState({ step: stepName, data }));
  };

  const placeOrder = useCallback(async () => {
    if (isPending) return;
    const orderPayload = getOrderPayload(steps);
    await placeRentalRequest(orderPayload);
  }, [steps]);

  useEffect(() => {
    clearPreloadPDFConfig();
    localStorage.removeItem("requestType");
  }, []);

  return (
    <OrderFlowContext.Provider
      value={{
        updateStep,
        updateOrder,
        placeOrder,
        currentStep,
        stepState: currentStepState,
        steps,
        orderLoading: isPending,
      }}
    >
      {children}
    </OrderFlowContext.Provider>
  );
};

OrderFlowProvider.propTypes = {
  children: PropTypes.node.isRequired
};